// src/pages/SettingsPage.js
/**
 * SettingsPage
 
 */

import React, { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { LABELLING } from "../config/labelling";
import { getAuth, signOut } from "firebase/auth";

// Choose ONE of the following depending on which option you implemented:
import { APP_VERSION, BUILD_DATE } from "../utils/version";
import { Link, useNavigate } from "react-router-dom";
import { LogoutIcon } from "../components/icons/LogoutIcon";
import { firebaseApp } from "../firebase/firebaseApp";
import LangSwitcher from "../components/LangSwitcher";
import { useUserDoc } from "../hooks/useUserDoc";
import { emailInitial } from "../utils/email";
import { useUserEmail } from "../hooks/useUserEmail";
import Spacer from "../components/ui/Spacer";
import { Row } from "../components/ui/Row";

function SettingsPage() {
  const { email: userEmail, loading: loadingEmail } = useUserEmail();

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  /** Sign user out and redirect to `/auth/login` */
  const handleSignOut = useCallback(() => {
    signOut(getAuth(firebaseApp))
      .then(() => navigate("/", { replace: true }))
      .catch(
        (err) => process.env.NODE_ENV === "development" && console.error(err)
      );
  }, [navigate]);

  const { data } = useUserDoc();

  // Account + plan state from Firestore
  const accountStatus = data?.accountStatus; // "registered" when a proper account exists
  const onboardingStep = data?.onboarding?.step;
  const isRegistered =
    accountStatus === "registered" ||
    onboardingStep === "accountCreated" ||
    onboardingStep === "paymentComplete";

  const currentPlanTier = (
    data?.entitlements?.plan ||
    data?.plan?.tier ||
    ""
  ).toLowerCase(); // "free" | "starter" | "performance" | ""
  const currentInterval =
    data?.plan?.interval || data?.entitlements?.interval || null; // "month" | "year" | null

  return (
    <div className="relative w-full max-w-lg h-fit flex flex-col p-4 gap-4 z-10 mx-auto">
      <div className="flex flex-row w-full justify-between gap-4">
        <LangSwitcher showLabelWhenClosed={true} />
        <button
          type="button"
          onClick={handleSignOut}
          aria-label="Sign out"
          className="small-outlined-button flex !flex-row !w-fit"
        >
          <LogoutIcon className="!h-6 fill-primary" />
          {t("common.signout", { defaultValue: "Sign out" })}{" "}
        </button>
      </div>
      <Spacer />
      <div className="flex flex-wrap gap-2 items-center justify-center">
        <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold">
          {emailInitial(userEmail)}
        </div>
        <div>{userEmail}</div>
      </div>
      <Spacer />
      <Row label={t("settings.info", { defaultValue: "Info" })}>
        <Link
          to="/info"
          className="flex flex-row justify-between items-center text-primary hover:italic focus:outline-none focus:ring-2 focus:ring-primary/50 rounded-md"
          aria-label={t("settings.infoAria", {
            defaultValue: "View information about your metrics",
          })}
        >
          {t("settings.infoLinkText", {
            defaultValue: "Learn about your metrics",
          })}
          <span
            className="material-symbols-outlined text-[24px] no-underline"
            aria-hidden="true"
          >
            chevron_right
          </span>
        </Link>
      </Row>{" "}
      <Spacer />
      <Row label={t("settings.feedback", { defaultValue: "Feedback" })}>
        <Link
          to="https://forms.gle/LTkNFy3AdorPhcTJ6"
          target="_blank"
          className="flex flex-row justify-between items-center text-primary hover:italic focus:outline-none focus:ring-2 focus:ring-primary/50 rounded-md"
          aria-label={t("settings.feedbackAria", {
            defaultValue: "Leave feedback for BreatheUltra",
          })}
        >
          {t("settings.feedbackLinkText", {
            defaultValue: "Leave feedback",
          })}
          <span
            className="material-symbols-outlined text-[24px] no-underline"
            aria-hidden="true"
          >
            chevron_right
          </span>
        </Link>
      </Row>
      <Spacer />
      <Row label={t("pay.currentPlan", { defaultValue: "Current plan:" })}>
        <Link
          to={"/payment"}
          className="flex flex-row justify-between items-center"
        >
          {currentPlanTier.charAt(0).toUpperCase() +
            currentPlanTier.slice(1).toLowerCase()}
          {currentPlanTier !== "free" && currentInterval
            ? `  (${currentInterval})`
            : ""}
          <span
            className="material-symbols-outlined text-[24px]"
            aria-hidden="true"
          >
            edit
          </span>
        </Link>
      </Row>
      <Spacer />
      {/* Labelling block */}
      <section
        aria-labelledby="labelling-heading"
        className="flex flex-col gap-3"
      >
        <div className="flex flex-col gap-4">
          {/* App name */}
          <Row label={t("labelling.appName")}>{LABELLING.APP_NAME}</Row>

          {/* Version (from package.json via env or version.json) */}
          <Row label={t("labelling.version")}>{APP_VERSION} (beta)</Row>

          {/* Date of manufacture / build date */}
          <Row
            label={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 205 255"
                className="fill-transparent h-6 lg:h-8 shrink-0"
                aria-hidden="true"
              >
                <path
                  stroke="#000"
                  strokeWidth="5"
                  d="M2.5 252.5v-120l25-50 25 50 25-50 25 50 25-50 25 50V2.5h50v250z"
                />
              </svg>
            }
          >
            {BUILD_DATE}
          </Row>

          {/* Manufacturer */}
          <Row
            label={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 205 255"
                className="fill-black h-6 lg:h-8 shrink-0"
                aria-hidden="true"
              >
                <path
                  stroke="#000"
                  strokeWidth="5"
                  d="M2.5 252.5v-120l25-50 25 50 25-50 25 50 25-50 25 50V2.5h50v250z"
                />
              </svg>
            }
          >
            <address className="flex flex-col not-italic leading-tight">
              <span>{t("company.name")}</span>
              <span>{t("company.address1")}</span>
              <span>{t("company.address2")}</span>
              <span>{t("company.address3")}</span>
            </address>
          </Row>
        </div>
      </section>
      <Spacer />
      <p className="muted-text ">© October 2025 · electronRx · Cambridge, UK</p>
    </div>
  );
}

export default memo(SettingsPage);
